import { ADVANCED_VIZ_TYPE, UNKNOWN_DATUM } from 'business/constants';
import type { VizType } from 'business/definitions';
import { pipe } from 'fp-ts/lib/function';
import { isContinuous } from 'helpers/chartHelper';
import { GEO_TYPES } from 'helpers/constants/geoTypes';
import { isTypeNumeric } from 'helpers/helpers';
import { getUniqueAggregation } from 'modules/decision/pages/Explore/components/Sentence/helpers';

type GetAvailableVizParams = {
    data: ReadonlyArray<ResponseDataT>;
    columns: ReadonlyArray<ResponseColumnT>;
    metrics: ReadonlyArray<QueryMetricT>;
    dimensions: ReadonlyArray<QueryDimensionT>;
};

/**
 * We consider that money columns cannot be un-effective.
 * For example if we ask for amount budgeted, it will remain the same
 * during the entire year despite the fact that it is an important column
 *
 * @returns a filtered list of columns
 */
const getEffectiveColumns = (
    columns: GetAvailableVizParams['columns'],
    data: GetAvailableVizParams['data'],
) =>
    columns.filter((col) => {
        const differentValues = Array.from(
            new Set(data.map((d) => d[col.key])),
        );
        if (col.type === 'money') {
            return (
                differentValues.filter((d) => d !== UNKNOWN_DATUM).length > 0
            );
        }
        return differentValues.length > 1 || col.isMetric;
    });

/**
 * Return true if there is no date or datetime column in a given list of columns
 *
 * @returns boolean
 */
const checkIfNoDateColumns = (columns: GetAvailableVizParams['columns']) =>
    columns.every((col) => !['date', 'datetime'].includes(col.type));

/**
 * Return true if there is at least a geo-column.
 */
const shouldEnableMapViz = (
    columns: GetAvailableVizParams['columns'],
): boolean => columns.some((col) => GEO_TYPES.includes(col.type));

/**
 * Return true if there is more than one dimension or if there is more than 2 columns and that they all are lists.
 */
const shouldEnablePivotTableViz = (
    columns: GetAvailableVizParams['columns'],
    dimensions: GetAvailableVizParams['dimensions'],
    isList: boolean,
): boolean => dimensions.length >= 1 || (isList && columns.length > 1);

/**
 * Check if we should only enable the table and map viz
 * @param columns
 * @param dimensions
 * @returns
 */
const shouldOnlyEnableTablesAndMap = (
    columns: GetAvailableVizParams['columns'],
) => {
    return (
        columns.filter(
            (column) =>
                !column.isMetric && column.type && isContinuous(column.type),
        ).length >= 2
    );
};

type AvailableVizWithContext = {
    availableViz: VizType[];
    context: Readonly<GetAvailableVizParams>;
};

/**
 * Check if we should enable the number, gauge and paragraph viz
 *
 * They are available only if there is a single metric in the request and only one data line.
 * The first column must not be a number.
 */
const checkNumberGaugeParagraph = (
    availableVizWithContext: AvailableVizWithContext,
): AvailableVizWithContext => {
    const {
        availableViz,
        context: { data, columns, metrics },
    } = availableVizWithContext;
    if (
        metrics.length === 1 &&
        data.length === 1 &&
        isTypeNumeric(columns[0].type)
    ) {
        return {
            ...availableVizWithContext,
            availableViz: [...availableViz, 'number', 'gauge', 'paragraph'],
        };
    }
    return availableVizWithContext;
};

/**
 * Check if we should enable the vertical and horizontal bar charts viz
 *
 * They are available when their is multiple columns in the data
 * and at least one of them is not a metic.
 */
const checkBarCharts = (
    availableVizWithContext: AvailableVizWithContext,
): AvailableVizWithContext => {
    const {
        availableViz,
        context: { columns, dimensions },
    } = availableVizWithContext;
    if (columns.length > 1 && dimensions.length > 0) {
        return {
            ...availableVizWithContext,
            availableViz: [
                ...availableViz,
                'bar',
                'horizontal_bar',
                'polar_bar',
            ],
        };
    }
    return availableVizWithContext;
};

/**
 * Check if we should enable the scatterplot, area and line charts viz
 *
 * They are available when their is multiple columns in the data
 * and at least one of them is not a metic. They also have a meaning only if there is at least two lines.
 */
const checkScatterplotAreaLine = (
    availableVizWithContext: AvailableVizWithContext,
): AvailableVizWithContext => {
    const {
        availableViz,
        context: { columns, dimensions, data },
    } = availableVizWithContext;
    if (columns.length > 1 && dimensions.length > 0 && data.length > 1) {
        return {
            ...availableVizWithContext,
            availableViz: [...availableViz, 'scatterplot', 'area', 'line'],
        };
    }
    return availableVizWithContext;
};

/**
 * Check if we should enable the pyramid viz.
 *
 * The pyramid chart is a specific case of bar chart. It is only available when there is three columns
 * and one of them is a category with exactly two distinct values.
 */
const checkPyramid = (
    availableVizWithContext: AvailableVizWithContext,
): AvailableVizWithContext => {
    const {
        availableViz,
        context: { columns, dimensions },
    } = availableVizWithContext;
    if (
        columns.length === 3 &&
        dimensions.length > 0 &&
        columns.some((col) => !isTypeNumeric(col.type)) &&
        columns.filter((col) => isTypeNumeric(col.type)).length === 2
    ) {
        return {
            ...availableVizWithContext,
            availableViz: [...availableViz, 'pyramid'],
        };
    }
    return availableVizWithContext;
};

/**
 * Return if round charts (pie, donut) should be enabled in the viz selection
 *
 * @returns boolean
 */
const shouldEnableRoundChart = ({
    data,
    columns,
    metrics,
    dimensions,
    hasNoDateEffectiveColumn,
}: GetAvailableVizParams & {
    hasNoDateEffectiveColumn: boolean;
}) => {
    // first case
    const hasMultipleMetricsButSingleLine =
        metrics.length > 1 && data.length === 1;
    if (hasMultipleMetricsButSingleLine) return true;

    // second case
    const hasNoMoreThanOneContinuousColumn =
        columns.filter(
            (col) => isTypeNumeric(col.type) && isContinuous(col.type),
        ).length <= 1;
    const hasSingleMetric = metrics.length === 1;
    const hasOneOrNoDimension = dimensions.length <= 1;
    // if there is a single data line but only one column, there won't be any X axis, and we need at least one
    const hasMultipleDatapointsOrOneDimension =
        data.length > 1 || (data.length === 1 && columns.length > 1);
    if (
        hasMultipleDatapointsOrOneDimension &&
        hasSingleMetric &&
        hasNoMoreThanOneContinuousColumn &&
        hasNoDateEffectiveColumn &&
        hasOneOrNoDimension
    )
        return true;
    return false;
};

/**
 * Check if we should enable the pie and donut charts viz
 *
 * There is two case where they are available:
 * - There is only one metric and multiple lines with at most one numeric & continuous column and no date column
 * - There is only one line and multiple metrics (no dimensions or not effective)
 */
const checkPieDonut = (
    availableVizWithContext: AvailableVizWithContext,
): AvailableVizWithContext => {
    const {
        availableViz,
        context: { data, columns, metrics, dimensions },
    } = availableVizWithContext;
    const effectiveColumns = getEffectiveColumns(columns, data);
    const hasNoDateEffectiveColumn = checkIfNoDateColumns(effectiveColumns);
    const enableRoundChart = shouldEnableRoundChart({
        data,
        columns,
        metrics,
        dimensions,
        hasNoDateEffectiveColumn,
    });
    if (enableRoundChart) {
        return {
            ...availableVizWithContext,
            availableViz: [...availableViz, 'pie', 'donut'],
        };
    }
    return availableVizWithContext;
};

/**
 * Check if we should enable the treemap viz
 */
const checkTreemap = (
    availableVizWithContext: AvailableVizWithContext,
): AvailableVizWithContext => {
    const {
        availableViz,
        context: { metrics, dimensions, columns },
    } = availableVizWithContext;
    const hasNoDateEffectiveColumn = checkIfNoDateColumns(columns);
    if (
        metrics.length > 0 &&
        dimensions.length > 1 &&
        columns.length > 2 &&
        hasNoDateEffectiveColumn
    ) {
        return {
            ...availableVizWithContext,
            availableViz: [...availableViz, 'treemap'],
        };
    }
    return availableVizWithContext;
};

/**
 * Get the available vizTypes given ordered columns.
 * @param  {ReadonlyArray<ResponseDataT>} data output of the get data query
 * @param  {ReadonlyArray<ResponseColumnT>} columns output of the get data query
 * @param  {ReadonlyArray<QueryMetricT>} metrics the query metrics
 * @param  {ReadonlyArray<QueryDimensionT>} dimensions the query dimensions
 * @return {VizType[]}  list of available viz
 */
export const getAvailableViz = (
    data: GetAvailableVizParams['data'],
    columns: GetAvailableVizParams['columns'],
    metrics: GetAvailableVizParams['metrics'],
    dimensions: GetAvailableVizParams['dimensions'],
): VizType[] => {
    const availableViz: VizType[] = ['table'];

    const isList = getUniqueAggregation(metrics) === 'list';
    if (shouldEnablePivotTableViz(columns, dimensions, isList))
        availableViz.push('pivot_table');

    // If the metric aggregation is a list, we only allow the pivot table and the table viz
    if (isList) return availableViz;

    if (shouldEnableMapViz(columns)) availableViz.push('map');
    if (shouldOnlyEnableTablesAndMap(columns)) return availableViz;

    const availableVizWithContext: AvailableVizWithContext = {
        availableViz,
        context: {
            data,
            columns,
            metrics,
            dimensions,
        } as const,
    };
    // We add the viz in the order we want them to appear, if conditions are met to display them
    return pipe(
        availableVizWithContext,
        checkNumberGaugeParagraph,
        checkBarCharts,
        checkScatterplotAreaLine,
        checkPyramid,
        checkPieDonut,
        checkTreemap,
    ).availableViz;
};

/**
 * Return the viz type suggested if it's available, or the first available.
 *
 * Useful when the filters on a query changes the dimensions of a viz -
 * for instance when a category dimension has only one value, the viz available are not the same.
 */
export const selectBestAvailableViz = (
    suggestedVizType: VizType,
    availableViz: VizType[],
): VizType => {
    if (
        availableViz.includes(suggestedVizType) ||
        // If we have an advanced viz we keep it no matter what
        suggestedVizType === ADVANCED_VIZ_TYPE
    ) {
        return suggestedVizType;
    }
    return availableViz[0];
};
